import { useEffect, useMemo, useRef, useState } from 'react';
import type { PaletteEntry } from '../../model/format.ts';
import { gameData, readyImage } from '../../data/gameData.ts';
import { customSheet, spritePixels, texturePixels } from '../../data/customTiles.ts';
import { styleData } from '../../terrain/index.ts';
import { SpriteThumb, useTextureVersion } from '../../terrain/ttShared.ts';
import { Icon } from '../widgets.tsx';
import { applyFlags, crop, type Img } from './img.ts';

export interface SourcePick {
  pe: PaletteEntry;
  flags: number;
}

const pinKey = (styleId: string) => `ple:te-pins:${styleId}`;

function loadPins(styleId: string): PaletteEntry[] {
  try {
    return JSON.parse(localStorage.getItem(pinKey(styleId)) ?? '[]');
  } catch {
    return [];
  }
}

/** Tileset sheet browser (pick a sprite, or drag over the sheet to copy part of it), the source tile and pinned references. */
export function SourcePane({ styleId, source, onSource, onPaste, onStart, onOpen }: { styleId: string; source: SourcePick | null; onSource: (s: SourcePick | null) => void; onPaste: (img: Img) => void; onStart: (img: Img) => void; onOpen: (s: SourcePick) => void }) {
  const d = styleData(styleId);
  const custom = customSheet(styleId);
  const [sheet, setSheet] = useState<'tileset' | 'custom'>('tileset');
  const [scale, setScale] = useState(2);
  const [pins, setPins] = useState<PaletteEntry[]>(() => loadPins(styleId));
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [hover, setHover] = useState<PaletteEntry | null>(null);
  const tv = useTextureVersion();
  const ref = useRef<HTMLCanvasElement>(null);
  const tex = sheet === 'custom' && custom ? custom.guid : d?.tex ?? '';
  const texDef = tex ? gameData().sprites.textures[tex] : undefined;

  useEffect(() => localStorage.setItem(pinKey(styleId), JSON.stringify(pins)), [pins, styleId]);

  const spriteAt = (x: number, y: number): PaletteEntry | null => {
    if (!texDef) return null;
    for (const s of texDef.sprites) {
      const top = texDef.h - s.r[1] - s.r[3];
      if (x >= s.r[0] && x < s.r[0] + s.r[2] && y >= top && y < top + s.r[3]) return { tex, sprite: s.n };
    }
    return null;
  };
  const sel = source && source.pe.tex === tex ? texDef?.sprites.find((s) => s.n === source.pe.sprite) : undefined;

  useEffect(() => {
    const c = ref.current;
    const img = readyImage(gameData().images.get(tex));
    if (!c || !texDef) return;
    c.width = texDef.w * scale;
    c.height = texDef.h * scale;
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    g.fillStyle = '#0d0d12';
    g.fillRect(0, 0, c.width, c.height);
    if (img) g.drawImage(img, 0, 0, c.width, c.height);
    g.strokeStyle = 'rgba(255,255,255,0.07)';
    for (const s of texDef.sprites) g.strokeRect(s.r[0] * scale + 0.5, (texDef.h - s.r[1] - s.r[3]) * scale + 0.5, s.r[2] * scale - 1, s.r[3] * scale - 1);
    const box = (s: { r: number[] }, color: string) => {
      g.strokeStyle = color;
      g.lineWidth = 2;
      g.strokeRect(s.r[0] * scale, (texDef.h - s.r[1] - s.r[3]) * scale, s.r[2] * scale, s.r[3] * scale);
      g.lineWidth = 1;
    };
    const hs = hover && texDef.sprites.find((s) => s.n === hover.sprite);
    if (hs) box(hs, 'rgba(255,255,255,0.6)');
    if (sel) box(sel, '#ffe066');
    if (drag) {
      const x = Math.min(drag.x0, drag.x1), y = Math.min(drag.y0, drag.y1);
      g.strokeStyle = '#4fc3ff';
      g.setLineDash([4, 3]);
      g.strokeRect(x * scale + 0.5, y * scale + 0.5, (Math.abs(drag.x1 - drag.x0) + 1) * scale - 1, (Math.abs(drag.y1 - drag.y0) + 1) * scale - 1);
      g.setLineDash([]);
    }
  }, [tex, texDef, scale, sel, drag, hover, tv]);

  const at = (e: React.PointerEvent): [number, number] => {
    const r = ref.current!.getBoundingClientRect();
    return [Math.floor((e.clientX - r.left) / scale), Math.floor((e.clientY - r.top) / scale)];
  };
  const srcImg = useMemo(() => {
    if (!source) return null;
    const p = spritePixels(source.pe);
    return p ? applyFlags(p, source.flags) : null;
  }, [source, tv]);

  if (!d) return null;
  return (
    <section className="te-source">
      <h4>Copy from tiles</h4>
      <div className="te-row">
        <div className="te-tabs inline">
          <button className={sheet === 'tileset' ? 'active' : ''} onClick={() => setSheet('tileset')}>Tileset sheet</button>
          <button className={sheet === 'custom' ? 'active' : ''} disabled={!custom?.tiles.length} onClick={() => setSheet('custom')}>Custom tiles ({custom?.tiles.length ?? 0})</button>
        </div>
        <span className="te-grow" />
        {[1, 2, 3, 4].map((k) => (
          <button key={k} className={`chip small ${scale === k ? 'active' : ''}`} onClick={() => setScale(k)}>{k}×</button>
        ))}
      </div>
      <div className="te-sheet">
        <canvas
          ref={ref}
          style={{ width: (texDef?.w ?? 0) * scale, height: (texDef?.h ?? 0) * scale }}
          onPointerDown={(e) => {
            const [x, y] = at(e);
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            setDrag({ x0: x, y0: y, x1: x, y1: y });
          }}
          onPointerMove={(e) => {
            const [x, y] = at(e);
            if (drag) setDrag({ ...drag, x1: x, y1: y });
            else {
              const h = spriteAt(x, y);
              if (h?.sprite !== hover?.sprite) setHover(h);
            }
          }}
          onPointerUp={() => {
            if (!drag) return;
            setDrag(null);
            const w = Math.abs(drag.x1 - drag.x0) + 1, h = Math.abs(drag.y1 - drag.y0) + 1;
            if (w * h <= 4) {
              // a tap: that sprite becomes the source and opens beside the canvas
              const pe = spriteAt(drag.x0, drag.y0);
              if (pe) {
                onSource({ pe, flags: 0 });
                onOpen({ pe, flags: 0 });
              }
              return;
            }
            const tp = texturePixels(tex);
            if (tp) onPaste(crop(tp, Math.min(drag.x0, drag.x1), Math.min(drag.y0, drag.y1), w, h));
          }}
          onPointerLeave={() => setHover(null)}
        />
      </div>
      <div className="muted small">Tap a sprite to open it beside your tile; drag over the sheet to paste that part (any size) as a floating piece.</div>
      {source && (
        <div className="te-src">
          <SpriteThumb pe={source.pe} flags={source.flags} size={48} />
          <div className="te-src-info">
            <span className="small">{source.pe.sprite}{source.flags ? ' (flipped)' : ''}</span>
            <div className="te-row">
              <button className="chip" onClick={() => onOpen(source)} title="Open it beside your tile to copy parts from it">Open beside</button>
              <button className="chip" onClick={() => srcImg && onStart(srcImg)} title="Replace the tile with this one (undo with Ctrl+Z)">Start from this</button>
              <button className="chip" onClick={() => srcImg && onPaste(srcImg)} title="Paste it as a floating piece"><Icon name="paste" size={14} /> Paste</button>
              <button className="chip" onClick={() => !pins.some((p) => p.tex === source.pe.tex && p.sprite === source.pe.sprite) && setPins([...pins, source.pe])} title="Keep it in the reference strip"><Icon name="pin" size={14} /> Pin</button>
              <button className="chip" onClick={() => onSource(null)}><Icon name="close" size={14} /></button>
            </div>
          </div>
        </div>
      )}
      {pins.length > 0 && (
        <>
          <h4>Pinned</h4>
          <div className="te-pins">
            {pins.map((p) => (
              <div key={p.tex + p.sprite} className="te-pin">
                <button onClick={() => { onSource({ pe: p, flags: 0 }); onOpen({ pe: p, flags: 0 }); }} title={`${p.sprite}: open beside`}>
                  <SpriteThumb pe={p} flags={0} size={48} />
                </button>
                <button className="te-x" title="Unpin" onClick={() => setPins(pins.filter((q) => q !== p))}>×</button>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
